/**
 * <s-product-thumbs> — PDP gallery thumbnails (merchant mock-up Oct 30).
 *
 * The main gallery is a native scroll-snap track; thumbnails jump to a slide, round arrows go to the
 * previous / next slide, and the thumb of the visible slide is marked with data-active + aria-current.
 *
 * Required markup (inside the element):
 *   [data-product-gallery]      — the horizontal scroll-snap track, one child per slide.
 *   [data-thumb="<index>"]      — thumbnail buttons.
 *   [data-thumbs-prev], [data-thumbs-next] — optional arrow buttons.
 *   [data-thumbs-track]         — optional scrollable thumbnail row (active thumb is kept in view).
 */
class SProductThumbs extends HTMLElement {
  #ac;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const thumb = target.closest('[data-thumb]');
      if (thumb) {
        this.go(Number(thumb.getAttribute('data-thumb')));
        return;
      }
      if (target.closest('[data-thumbs-prev]')) this.go(this.#index - 1);
      if (target.closest('[data-thumbs-next]')) this.go(this.#index + 1);
    }, opts);

    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#sync());
    }, { ...opts, passive: true });

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get slides() {
    return Array.from(this.track?.children ?? []);
  }

  go(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const next = (index + slides.length) % slides.length;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slides[next].offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(next);
  }

  #sync() {
    const width = this.track.clientWidth || 1;
    this.#setActive(Math.round(this.track.scrollLeft / width));
  }

  #setActive(index) {
    this.#index = Math.max(0, Math.min(index, this.slides.length - 1));
    for (const thumb of this.querySelectorAll('[data-thumb]')) {
      const active = Number(thumb.getAttribute('data-thumb')) === this.#index;
      thumb.toggleAttribute('data-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        const row = this.querySelector('[data-thumbs-track]');
        if (row) {
          const left = thumb.offsetLeft - row.offsetLeft - (row.clientWidth - thumb.clientWidth) / 2;
          row.scrollTo({ left, behavior: 'auto' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-product-thumbs')) customElements.define('s-product-thumbs', SProductThumbs);
